import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import { EmojiEvents as TrophyIcon } from '@mui/icons-material';
import { formatGameDate, formatGameTime } from '../../utils/dateUtils';


const BracketMatch = ({ match, label }) => {
  if (!match) return null;

  const isCompleted = match.homeScore != null && match.awayScore != null;
  const homeWon = isCompleted && match.homeScore > match.awayScore;
  const awayWon = isCompleted && match.awayScore > match.homeScore;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
            {label}
          </Typography>
          {isCompleted ? (
            <Chip label="Final" size="small" color="success" variant="outlined" clickable={false} onClick={() => {}} />
          ) : (
            <Chip label="Scheduled" size="small" color="default" variant="outlined" clickable={false} onClick={() => {}} />
          )}
        </Box>

        {/* Teams */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: homeWon ? 'bold' : 'normal', color: homeWon ? 'success.main' : 'text.primary' }}
            >
              {match.homeTeamName || 'TBD'}
            </Typography>
            {isCompleted && (
              <Typography variant="h6" sx={{ fontWeight: 'bold', minWidth: 24, textAlign: 'center' }}>
                {match.homeScore}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: awayWon ? 'bold' : 'normal', color: awayWon ? 'success.main' : 'text.primary' }}
            >
              {match.awayTeamName || 'TBD'}
            </Typography>
            {isCompleted && (
              <Typography variant="h6" sx={{ fontWeight: 'bold', minWidth: 24, textAlign: 'center' }}>
                {match.awayScore}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Date */}
        <Typography variant="body2" color="text.secondary">
          {formatGameDate(match.start)} at {formatGameTime(match.start)}
        </Typography>

        {/* Winner */}
        {match.winnerTeamName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <TrophyIcon fontSize="small" sx={{ color: '#FFD700' }} />
            <Typography variant="body2" fontWeight="bold" color="success.main">
              Winner: {match.winnerTeamName}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const PlayoffBracketSection = ({ bracket }) => {
  if (!bracket) return null;

  const { semiFinals, final: finalMatch } = bracket;
  const hasContent = (semiFinals && semiFinals.length > 0) || finalMatch;

  if (!hasContent) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TrophyIcon color="primary" />
        <Typography variant="h5" component="h2" fontWeight="bold">
          Playoff Bracket
        </Typography>
      </Box>

      <Stack spacing={0}>
        {/* Semi-Finals */}
        {semiFinals && semiFinals.length > 0 && (
          <Box>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Semi-Finals
            </Typography>
            {semiFinals.map((match, index) => (
              <BracketMatch key={match.gameId} match={match} label={`Semi-Final ${index + 1}`} />
            ))}
          </Box>
        )}

        {/* Final */}
        {finalMatch && (
          <Box>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Final
            </Typography>
            <BracketMatch match={finalMatch} label="Final" />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default PlayoffBracketSection;
